import React, { useState, useEffect, useCallback } from "react";
import { Modal, Progress, Select, Button, Typography, Space, Alert } from "antd";
import { DownloadOutlined, VideoCameraOutlined } from "@ant-design/icons";
import axiosInstance from "../../../../../helpers/axiosConfig";

const { Text } = Typography;

const RESOLUTION_OPTIONS = [
  { label: "1080p (1920x1080)", value: "1920x1080" },
  { label: "720p (1280x720)", value: "1280x720" },
  { label: "480p (854x480)", value: "854x480" },
];

const STATUS_LABELS = {
  pending: "Waiting in queue...",
  downloading: "Downloading files...",
  rendering: "Rendering video...",
  uploading: "Uploading result...",
  completed: "Export complete!",
  failed: "Export failed",
};

const POLL_INTERVAL_MS = 3000;

export const ExportModal = ({ visible, onClose, collectionId }) => {
  const [resolution, setResolution] = useState("1920x1080");
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [starting, setStarting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setJobId(null);
      setStatus(null);
      setProgress(0);
      setError(null);
      setDownloadUrl(null);
      setStarting(false);
    }
  }, [visible]);

  // Poll for status updates
  useEffect(() => {
    if (!jobId || status === "completed" || status === "failed") return;

    const interval = setInterval(async () => {
      try {
        const res = await axiosInstance.get(`render/status/${jobId}`);
        setStatus(res.data.status);
        setProgress(res.data.progress);

        if (res.data.status === "failed") {
          setError(res.data.error || "Unknown error occurred");
          clearInterval(interval);
        }

        if (res.data.status === "completed") {
          clearInterval(interval);
          // Fetch the download URL
          try {
            const dlRes = await axiosInstance.get(`render/download/${jobId}`);
            setDownloadUrl(dlRes.data.downloadUrl);
          } catch (dlErr) {
            setError("Failed to get download link");
          }
        }
      } catch (err) {
        // Don't clear interval on transient errors
        console.error("Error polling render status:", err);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [jobId, status]);

  const handleStartExport = useCallback(async () => {
    setStarting(true);
    setError(null);

    try {
      const [width, height] = resolution.split("x").map(Number);
      const res = await axiosInstance.post(`render/${collectionId}`, {
        width,
        height,
      });
      setJobId(res.data.jobId);
      setStatus(res.data.status);
      setProgress(0);
    } catch (err) {
      console.error("Export start error:", err.response?.status, err.response?.data, err);
      const status = err.response?.status;
      const serverMsg = err.response?.data?.message;
      const message = status
        ? `${status}: ${serverMsg || err.message}`
        : err.message || "Failed to start export";
      setError(message);
    } finally {
      setStarting(false);
    }
  }, [collectionId, resolution]);

  const handleDownload = useCallback(() => {
    if (downloadUrl) {
      window.open(downloadUrl, "_blank");
    }
  }, [downloadUrl]);

  const isInProgress = jobId && status && status !== "completed" && status !== "failed";

  return (
    <Modal
      title={
        <Space>
          <VideoCameraOutlined />
          <span>Export to MP4</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      maskClosable={!isInProgress}
      closable={!isInProgress}
    >
      {!jobId ? (
        // Initial state: resolution picker + start button
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Text strong>Resolution</Text>
            <Select
              value={resolution}
              onChange={setResolution}
              options={RESOLUTION_OPTIONS}
              style={{ width: "100%", marginTop: 8 }}
            />
          </div>

          <Text type="secondary">
            Your compilation will be rendered as an MP4 video with each slide
            shown for its configured duration. This may take a few minutes
            depending on the number of files.
          </Text>

          {error && (
            <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} />
          )}

          <Button
            type="primary"
            icon={<VideoCameraOutlined />}
            onClick={handleStartExport}
            loading={starting}
            block
            size="large"
          >
            Start Export
          </Button>
        </Space>
      ) : (
        // In-progress or completed state
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Progress
            percent={progress}
            status={status === "failed" ? "exception" : status === "completed" ? "success" : "active"}
            strokeColor={status === "completed" ? "#52c41a" : undefined}
          />

          <Text strong style={{ display: "block", textAlign: "center" }}>
            {STATUS_LABELS[status] || status}
          </Text>

          {error && (
            <Alert message={error} type="error" showIcon />
          )}

          {status === "completed" && downloadUrl && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              block
              size="large"
            >
              Download MP4
            </Button>
          )}

          {status === "completed" && (
            <Button onClick={onClose} block>
              Close
            </Button>
          )}

          {status === "failed" && (
            <Button onClick={() => {
              setJobId(null);
              setStatus(null);
              setProgress(0);
              setError(null);
            }} block>
              Try Again
            </Button>
          )}
        </Space>
      )}
    </Modal>
  );
};
